import React, { SyntheticEvent, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useMount, useUnmount } from "react-use";
import AddIcon from "@mui/icons-material/Add";
import AttachmentIcon from "@mui/icons-material/AttachFile";
import BurstModeIcon from "@mui/icons-material/BurstMode";
import { Box, Button, Tab, Tabs } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { omit } from "lodash";

import analytics from "fond/analytics";
import { useGetAccountModulesQuery } from "fond/api";
import { BulkAttachmentModal } from "fond/bulkAttachments";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { getCurrentProject } from "fond/project";
import { uploadAttachmentAbort } from "fond/redux/attachments";
import { AttachmentEntityType, AttachmentFilterType, MultiProject, Project, SelectedFeature, Store } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";
import { projectUsesVectorTiles } from "fond/utils/project";
import { Modal } from "fond/widgets";

import { FeatureAttachmentList } from "./AttachmentList/FeatureAttachmentList";
import { ProjectAttachmentList } from "./AttachmentList/ProjectAttachmentList";
import AttachmentStaging from "./AttachmentStaging/AttachmentStaging";
import AttachmentUpload from "./AttachmentUpload/AttachmentUpload";
import { AttachmentEventLocation, ModalView } from "./types";

import { AttachmentModalContent, AttachmentModalDataGrid } from "./AttachmentModal.styles";

type AttachmentModalProps = {
  onClose: () => void;
  isOpen: boolean;
  attachmentEntityType?: AttachmentEntityType;
  initialView?: Extract<ModalView, "list" | "staging">;
};

const getFeatureId = (attachmentEntityType: string, project: Project | MultiProject, selectedFeature: SelectedFeature): string | null => {
  if (attachmentEntityType !== "Feature") {
    return null;
  }

  if (projectUsesVectorTiles(project)) {
    return selectedFeature?.feature?.id?.toString() ?? null;
  }

  return selectedFeature?.featureId?.toString() ?? null;
};

const tabContents: {
  label: string;
  stateLocation: string;
  value: AttachmentFilterType;
}[] = [
  {
    label: "Project",
    stateLocation: "project",
    value: "project",
  },
  {
    label: "Features",
    stateLocation: "features",
    value: "layer_feature",
  },
];

const AttachmentModal: React.FC<AttachmentModalProps> = ({
  onClose,
  isOpen,
  attachmentEntityType = "Project",
  initialView,
}: AttachmentModalProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const uploadAttachmentAction = attachmentEntityType === "Project" ? Actions.PROJECT_UPLOAD_ATTACHMENT : Actions.FEATURE_UPLOAD_ATTACHMENT;
  const canUpload = usePermissionCheck(uploadAttachmentAction, project.Permission.Level);
  const { selectedFeature } = useSelector((state: Store) => state.project);
  const { data: accountModules } = useGetAccountModulesQuery(project?.Account.ID ?? skipToken);
  const canPerformAttachmentsBulkImport = accountModuleCheck(accountModules, Actions.ATTACHMENTS_BULK_IMPORT);
  const permissionLevel = project?.Permission.Level;

  /*
   * Modal view control.
   */
  const [modalView, setModalView] = useState<ModalView>(initialView ?? "list");

  useMount(() => analytics.track("attachments.modal.opened", { ...omit(baseAnalyticsProps, "location"), view: modalView }));
  useUnmount(() => analytics.track("attachments.modal.closed", { ...omit(baseAnalyticsProps, "location"), view: modalView }));

  const openList = () => setModalView("list");
  const openStaging = () => setModalView("staging");
  const openUploads = () => {
    setModalView("upload");
    analytics.track("attachments.upload.started", { ...baseAnalyticsProps, count: stagedFiles.length });
  };

  const listView = modalView === "list";
  const stagingView = modalView === "staging";
  const uploadsView = modalView === "upload";

  /*
   * File upload control.
   */
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const abortAllUploads = () => {
    for (const file of stagedFiles) {
      dispatch(uploadAttachmentAbort(file));
    }
    analytics.track("attachments.upload.cancelled", { ...baseAnalyticsProps, count: stagedFiles.length });
  };

  const [selectedTabValue, setSelectedTabValue] = useState<AttachmentFilterType>(
    tabContents.find((content) => content.stateLocation === location.state?.tab)?.value || "project"
  );

  const featureId = getFeatureId(attachmentEntityType, project, selectedFeature);

  const baseAnalyticsProps = useMemo(() => {
    const props: {
      context: AttachmentEntityType;
      location: AttachmentEventLocation;
      filterType?: AttachmentFilterType;
      featureId?: string;
      projectId: string;
    } = {
      context: attachmentEntityType,
      location: "modal",
      projectId: project.ID,
    };
    if (attachmentEntityType === "Project") {
      props.filterType = selectedTabValue;
    }
    if (attachmentEntityType === "Feature" && featureId) {
      props.featureId = featureId;
    }
    return props;
  }, [attachmentEntityType, featureId, project.ID, selectedTabValue]);

  const handleOnTabSelect = (_: SyntheticEvent<Element, Event>, value: AttachmentFilterType) => {
    setSelectedTabValue(value!);
    analytics.track("attachments.tab.clicked", { ...omit(baseAnalyticsProps, "location"), filterType: value });
  };
  const [isBulkAttachmentModalOpen, setBulkAttachmentModalOpen] = React.useState<boolean>(false);

  /**
   * Handles opening the Bulk Attachments modal
   */
  const handleBulkAttachment = () => {
    setBulkAttachmentModalOpen(true);
    analytics.track("attachments.bulk.modal.opened", baseAnalyticsProps);
  };

  const onBulkModalClose = () => {
    setBulkAttachmentModalOpen(false);
    analytics.track("attachments.bulk.modal.closed", baseAnalyticsProps);
  };
  const handleUploadClick = () => {
    openStaging();
    analytics.track("attachments.upload.clicked", baseAnalyticsProps);
  };

  const handleCancelStaging = () => {
    openList();
    analytics.track("attachments.staging.cancel.clicked", baseAnalyticsProps);
  };

  const handleCloseUploadsView = () => {
    openList();
    analytics.track("attachments.upload.close.clicked", baseAnalyticsProps);
  };

  return (
    <Modal
      data-testid="attachment-modal"
      variant="primary"
      open={isOpen}
      onClose={() => onClose()}
      header={`${attachmentEntityType === "Project" ? "File" : "Feature"} Attachments`}
      headerIcon={<AttachmentIcon />}
      disableBackdropClick={stagingView || uploadsView}
      DialogContentProps={{
        sx: { display: "flex" },
      }}
      PaperProps={{ style: { minHeight: "80vh" } }}
      content={
        <AttachmentModalContent>
          {listView && (
            <>
              {attachmentEntityType === "Project" && (
                <Tabs onChange={handleOnTabSelect} value={selectedTabValue}>
                  {tabContents.map((tab) => (
                    <Tab key={tab.label} label={tab.label} value={tab.value} data-testid={`tab-${tab.value}`} />
                  ))}
                </Tabs>
              )}
              {canUpload && selectedTabValue === "project" && (
                <Box mt={1}>
                  <Button
                    data-testid="attachment-modal-staging-btn"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleUploadClick}
                  >
                    Upload a file
                  </Button>
                </Box>
              )}
              {selectedTabValue === "layer_feature" && canPerformAttachmentsBulkImport && permissionLevel && (
                <Box mt={1}>
                  <Button
                    data-testid="attachment-modal-bulk-import-button"
                    variant="contained"
                    color="primary"
                    startIcon={<BurstModeIcon />}
                    onClick={handleBulkAttachment}
                  >
                    Bulk upload
                  </Button>
                </Box>
              )}
            </>
          )}
          <AttachmentModalDataGrid>
            <Box flex="1" width="100%">
              {listView && selectedTabValue === "project" && (
                <ProjectAttachmentList project={project} attachmentEntityType={attachmentEntityType} featureId={featureId} />
              )}
              {listView && selectedTabValue === "layer_feature" && (
                <FeatureAttachmentList project={project} attachmentEntityType={attachmentEntityType} featureId={featureId} />
              )}
              {stagingView && <AttachmentStaging attachmentEntityType={attachmentEntityType} onAcceptedStage={setStagedFiles} />}
              {uploadsView && <AttachmentUpload files={stagedFiles} onIsUploading={setIsUploading} featureId={featureId} />}
            </Box>
            <BulkAttachmentModal onClose={onBulkModalClose} isOpen={isBulkAttachmentModalOpen} />
          </AttachmentModalDataGrid>
        </AttachmentModalContent>
      }
      actions={
        <>
          {listView && (
            <Button data-testid="attachment-modal-close-btn" onClick={() => onClose()}>
              Close
            </Button>
          )}
          {stagingView && (
            <>
              <Button color="inherit" data-testid="attachment-modal-staging-cancel-btn" onClick={handleCancelStaging}>
                Cancel
              </Button>
              <Button
                data-testid="attachment-modal-staging-upload-btn"
                variant="contained"
                color="primary"
                disabled={stagedFiles.length === 0}
                onClick={openUploads}
              >
                Upload
              </Button>
            </>
          )}
          {uploadsView && (
            <>
              <Button color="inherit" data-testid="attachment-modal-upload-cancel-all-btn" onClick={abortAllUploads} disabled={!isUploading}>
                Cancel All
              </Button>
              <Button data-testid="attachment-modal-upload-close-btn" onClick={handleCloseUploadsView} disabled={isUploading}>
                Close
              </Button>
            </>
          )}
        </>
      }
    />
  );
};

export default AttachmentModal;
